import { Text, Switch, Select, Input, Tooltip } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import { GET_FREQUENCY_DEFAULTS } from 'src/graphql/automatedRules.graphql'
import IconInfoCircle from 'src/icons/IconInfoCircle'

import { FREQUENCY_LABELS, TRIGGER_DELAY_LABELS } from '../utils/utils'

const AutomatedRuleScheduling = ({
  isScheduling,
  setIsScheduling,
  resetScheduling,
  selectedExecutionLimit,
  setSelectedExecutionLimit,
  selectedFrequency,
  setSelectedFrequency,
  selectedTriggerDelay,
  setSelectedTriggerDelay,
  selectedTriggerDelayInt,
  setSelectedTriggerDelayInt,
}) => {
  const frequencyOptions = Object.keys(FREQUENCY_LABELS).map((key) => ({
    label: FREQUENCY_LABELS[key],
    value: key,
  }))

  // Pull the frequency → default execution limit map from the backend so the
  // UI cap stays in sync with what the server actually enforces. Apollo
  // caches this after the first call so subsequent opens are instant.
  const { data: frequencyDefaultsData } = useQuery(GET_FREQUENCY_DEFAULTS, {
    fetchPolicy: 'cache-first',
  })
  const executionLimitByFrequency: Record<string, number> =
    (frequencyDefaultsData?.automatedRuleFrequencyDefaults ?? []).reduce(
      (acc, { frequency, defaultExecutionLimit }) => {
        acc[frequency] = defaultExecutionLimit
        return acc
      },
      {} as Record<string, number>
    )

  const triggerDelayOptions = Object.keys(TRIGGER_DELAY_LABELS).map((key) => ({
    label: TRIGGER_DELAY_LABELS[key],
    value: key,
  }))

  return (
    <div className="space-y-4">
      {/* Header */}
      <Text
        component="span"
        size="xs"
        fw={400}
        className="flex flex-row items-center justify-between text-doubleNickel-gray-500"
      >
        SCHEDULING
        <Switch
          size="sm"
          checked={isScheduling}
          onChange={(e) => {
            if (!e.currentTarget.checked) resetScheduling()
            setIsScheduling((prev) => !prev)
          }}
        />
      </Text>

      {/* Scheduling options */}
      {isScheduling && (
        <div className="space-y-3">
          {/* Delay before first run */}
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-1">
              <Text size="xs" component="div">
                Initial Delay
                <span className="text-doubleNickel-error-500">{' *'}</span>
              </Text>
              <Tooltip
                label="Delay before the rule’s first execution."
                position="bottom"
                withArrow
                arrowPosition="center"
              >
                <div>
                  <IconInfoCircle className="h-3 w-3 fill-none stroke-doubleNickel-brand-500" />
                </div>
              </Tooltip>
            </div>
            <div className="flex flex-row items-center gap-2">
              <Input
                type="number"
                placeholder="1"
                size="xs"
                variant="unstyled"
                classNames={{ input: 'text-xs text-right w-[60px]' }}
                value={selectedTriggerDelayInt ?? ''}
                onChange={(e) => {
                  const val = e.currentTarget.value
                  setSelectedTriggerDelayInt(val === '' ? null : Number(val))
                }}
                onBlur={(e) => {
                  const val = e.currentTarget.value

                  // Clean up invalid or padded input
                  if (val === '' || Number(val) <= 0) {
                    setSelectedTriggerDelayInt(1) // default fallback, optional
                    return
                  }

                  // Remove leading zeros safely
                  const normalized = String(parseInt(val, 10))
                  setSelectedTriggerDelayInt(Number(normalized))
                }}
                min={1}
                step={1}
              />
              <Select
                size="xs"
                data={triggerDelayOptions}
                placeholder="Unit"
                variant="unstyled"
                classNames={{ input: 'text-xs text-right w-[110px]' }}
                value={selectedTriggerDelay}
                onChange={setSelectedTriggerDelay}
              />
            </div>
          </div>

          {/* Frequency */}
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-1">
              <Text size="xs" component="div">
                Run Frequency
                <span className="text-doubleNickel-error-500">{' *'}</span>
              </Text>
              <Tooltip
                label="Defines how often the rule runs."
                position="bottom"
                withArrow
                arrowPosition="center"
              >
                <div>
                  <IconInfoCircle className="h-3 w-3 fill-none stroke-doubleNickel-brand-500" />
                </div>
              </Tooltip>
            </div>
            <Select
              size="xs"
              data={frequencyOptions}
              placeholder="Select frequency"
              variant="unstyled"
              classNames={{ input: 'text-xs text-right w-[130px]' }}
              value={selectedFrequency}
              onChange={setSelectedFrequency}
            />
          </div>

          {/* Execution Limit */}
          {(() => {
            // Frequency-aware cap mirrors the backend's getDefaultExecutionLimit.
            // Without it the form silently accepts values above the cap, and
            // the backend caps them on save with no user feedback (ENG-4169).
            const executionLimitCap =
              executionLimitByFrequency[selectedFrequency] ?? 1
            const isOverCap =
              selectedExecutionLimit != null &&
              selectedExecutionLimit > executionLimitCap
            return (
              <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Text size="xs" component="div">
                      Maximum Runs
                      <span className="text-doubleNickel-error-500">{' *'}</span>
                    </Text>
                    <Tooltip
                      label={
                        selectedFrequency
                          ? `Total executions allowed. Max ${executionLimitCap} for ${FREQUENCY_LABELS[selectedFrequency]}.`
                          : 'Specifies the total number of executions allowed.'
                      }
                      position="bottom"
                      withArrow
                      arrowPosition="center"
                    >
                      <div>
                        <IconInfoCircle className="h-3 w-3 fill-none stroke-doubleNickel-brand-500" />
                      </div>
                    </Tooltip>
                  </div>
                  <Input
                    type="number"
                    placeholder={String(executionLimitCap)}
                    size="xs"
                    variant="unstyled"
                    classNames={{
                      input: `text-xs text-right w-[60px] ${isOverCap ? 'text-doubleNickel-error-500' : ''}`,
                    }}
                    min={1}
                    max={executionLimitCap}
                    value={selectedExecutionLimit ?? ''}
                    onChange={(e) => {
                      const val = e.currentTarget.value
                      setSelectedExecutionLimit(val === '' ? null : Number(val))
                    }}
                    onBlur={(e) => {
                      // Snap back into the valid range. The backend caps too,
                      // but doing it client-side surfaces the value the rule
                      // will actually save with — otherwise the user types 7,
                      // saves, and only sees "max 5" on reopening the form.
                      const val = e.currentTarget.value
                      if (val === '') return
                      const n = Number(val)
                      if (n > executionLimitCap) {
                        setSelectedExecutionLimit(executionLimitCap)
                      } else if (n < 1) {
                        setSelectedExecutionLimit(1)
                      }
                    }}
                  />
                </div>
                {isOverCap && (
                  <p className="text-right text-[10px] text-doubleNickel-error-500">
                    Max is {executionLimitCap} for{' '}
                    {FREQUENCY_LABELS[selectedFrequency] ?? 'this frequency'}.
                  </p>
                )}
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}

export default AutomatedRuleScheduling
